import { useState, Fragment } from 'react';
import { noop, map } from 'lodash';
import styled from '@emotion/styled';
import { Tab } from '@mui/material';
import { ThemeProps } from '@theme/base';
import { Component as ComponentContainer } from '@styles/layout';
import { Tabs as MuiTabs } from '@styles/material-ui';
import { GenericComponent } from '@model/content';
import { Icon } from '@components/common/generic-icon';
import { ContentTabs } from '@components/common/content-tabs';
import { mq } from '@styles/breakpoints';
import { getNestedComponents } from '@util/complex-components';

interface TabContentWrapperProps extends ThemeProps {
  isHidden: boolean;
}
const TabContentWrapper: any = styled.div(({ theme, isHidden }: TabContentWrapperProps) => ({
  display: isHidden ? 'none' : 'block',
  overflow: 'hidden',
  marginLeft: -theme.custom.spacing.large,
  marginRight: -theme.custom.spacing.large,
  [mq.large]: {
    paddingLeft: theme.custom.spacing.xxxLarge,
    paddingRight: theme.custom.spacing.xxxLarge
  }
}));

const Tabs = styled(MuiTabs)(({ theme }) => ({
  marginLeft: -theme.custom.spacing.large,
  marginRight: -theme.custom.spacing.large,
  ['.MuiTabs-flexContainer']: {
    gap: theme.custom.spacing.small,
    marginBottom: theme.custom.spacing.large,
    overflowX: 'auto',
    paddingLeft: theme.custom.spacing.large,
    paddingRight: theme.custom.spacing.large,
    ['::-webkit-scrollbar']: { display: 'none' },
    [mq.small]: {
      justifyContent: 'center'
    }
  },
  ['.MuiTabs-indicator']: {
    display: 'none'
  },
  ['.MuiTab-root']: {
    alignItems: 'center',
    border: `1px solid ${theme.custom.colors.group2.base}`,
    borderRadius: 30,
    color: theme.custom.colors.group2.base,
    display: 'flex',
    flexDirection: 'row',
    ['i']: {
      color: theme.custom.colors.group2.base,
      fontSize: '2rem',
      marginBottom: 0,
      marginRight: theme.custom.spacing.xSmall
    },
    ['&.Mui-selected']: {
      backgroundColor: theme.custom.colors.group2.base,
      color: theme.custom.colors.white,
      ['i']: {
        color: theme.custom.colors.white
      }
    }
  }
}));

export const TabGroupContainer = ({ data, identifier }: GenericComponent) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const updateTab = ({ target }, idx) => {
    setCurrentIndex(idx);
    try {
      target.parentElement.scroll({
        left: target.offsetLeft - 20,
        behavior: 'smooth'
      });
    } catch (e) {
      noop(e);
    }
  };

  const tabComponents = getNestedComponents(data || []);

  if (identifier === 'standard') {
    return (
      <ComponentContainer>
        <ContentTabs tabLabels={map(tabComponents, 'title')}>
          {tabComponents.map(({ title, components }) => (
            <div key={title}>
              {components.map(({ renderable }, idxx) => (
                <Fragment key={idxx}>{renderable}</Fragment>
              ))}
            </div>
          ))}
        </ContentTabs>
      </ComponentContainer>
    );
  }

  return (
    <ComponentContainer>
      <Tabs value={currentIndex} onChange={updateTab}>
        {tabComponents.map(({ title, icon }, idx) => (
          <Tab key={title || idx} label={title || ''} value={idx} icon={<Icon name={icon} />} />
        ))}
      </Tabs>
      {tabComponents.map(({ title, components }, idx) => (
        <TabContentWrapper key={title} isHidden={idx !== currentIndex}>
          {components.map(({ renderable }, idxx) => (
            <Fragment key={idxx}>{renderable}</Fragment>
          ))}
        </TabContentWrapper>
      ))}
    </ComponentContainer>
  );
};
